import React, { useRef, useState, useEffect } from 'react';

interface ChromaGridProps {
  children: React.ReactNode;
  className?: string;
  gridSize?: number;
  colors?: string[];
  animationSpeed?: number;
  intensity?: number;
}

/**
 * ChromaGrid - A component with animated chromatic grid background effect
 * Creates a dynamic, colorful grid that responds to hover
 */
export const ChromaGrid: React.FC<ChromaGridProps> = ({
  children,
  className = '',
  gridSize = 30,
  colors = ['#5260FF', '#7B68FF', '#4150DD', '#8B9FFF'],
  animationSpeed = 4,
  intensity = 0.5,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setMousePosition({ x, y });
    };

    if (isHovered) {
      container.addEventListener('mousemove', handleMouseMove);
    }

    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isHovered]);

  const gridPattern = `
    repeating-linear-gradient(
      0deg,
      rgba(82, 96, 255, 0.08) 0px,
      transparent 1px,
      transparent ${gridSize}px,
      rgba(82, 96, 255, 0.08) ${gridSize + 1}px
    ),
    repeating-linear-gradient(
      90deg,
      rgba(82, 96, 255, 0.08) 0px,
      transparent 1px,
      transparent ${gridSize}px,
      rgba(82, 96, 255, 0.08) ${gridSize + 1}px
    )
  `;

  const gradientColors = colors.join(', ');
  const gradientOverlay = `radial-gradient(
    circle at ${mousePosition.x}% ${mousePosition.y}%,
    ${colors[0]}${Math.round(intensity * 180).toString(16).padStart(2, '0')},
    ${colors[1]}${Math.round(intensity * 100).toString(16).padStart(2, '0')} 35%,
    transparent 70%
  )`;

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        transition: `all ${animationSpeed * 100}ms ease-out`,
      }}
    >
      {/* Static Grid Background */}
      <div
        className="absolute inset-0 pointer-events-none transition-opacity duration-300"
        style={{
          backgroundImage: gridPattern,
          opacity: isHovered ? 0.8 : 0.5,
        }}
      />

      {/* Animated Gradient Overlay */}
      <div
        className="absolute inset-0 pointer-events-none transition-opacity duration-500"
        style={{
          backgroundImage: gradientOverlay,
          opacity: isHovered ? 0.8 : 0.3,
        }}
      />

      {/* Animated Border Glow */}
      <div
        className="absolute inset-0 pointer-events-none transition-opacity duration-500"
        style={{
          opacity: isHovered ? 0.7 : 0.2,
          background: `linear-gradient(135deg, ${colors[0]}60, ${colors[1]}60, ${colors[2]}60, ${colors[3]}60)`,
          backgroundSize: '400% 400%',
          animation: `chromaShift ${animationSpeed}s ease infinite`,
          WebkitMaskImage: 'linear-gradient(black, black) content-box, linear-gradient(black, black)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
          padding: '2px',
        }}
      />

      {/* Content */}
      <div className="relative z-10">
        {children}
      </div>

      <style>{`
        @keyframes chromaShift {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
      `}</style>
    </div>
  );
};
